/**
 * Currency formatting utilities
 * Supports multiple currencies with proper symbols and formatting
 */

export type CurrencyCode = 'USD' | 'INR' | 'EUR' | 'GBP';

export interface CurrencyConfig {
  symbol: string;
  code: string;
  decimals: number;
  position: 'before' | 'after';
}

const CURRENCY_CONFIGS: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    symbol: '$',
    code: 'USD',
    decimals: 2,
    position: 'before',
  },
  INR: {
    symbol: '₹',
    code: 'INR',
    decimals: 2,
    position: 'before',
  },
  EUR: {
    symbol: '€',
    code: 'EUR',
    decimals: 2,
    position: 'before',
  },
  GBP: {
    symbol: '£',
    code: 'GBP',
    decimals: 2,
    position: 'before',
  },
};

/**
 * Format a number as currency with the appropriate symbol
 * @param amount - The amount to format
 * @param currency - The currency code (default: USD)
 * @param showCode - Whether to show the currency code after the amount (default: false)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: CurrencyCode = 'USD',
  showCode: boolean = false
): string {
  const config = CURRENCY_CONFIGS[currency] || CURRENCY_CONFIGS.USD;
  
  // Format the number with commas and decimals
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  });

  // Build the formatted string
  let result = '';
  if (config.position === 'before') {
    result = `${config.symbol}${formattedAmount}`;
  } else {
    result = `${formattedAmount}${config.symbol}`;
  }

  // Add currency code if requested
  if (showCode) {
    result += ` ${config.code}`;
  }

  return result;
}

/**
 * Get the currency symbol for a given currency code
 * @param currency - The currency code
 * @returns The currency symbol
 */
export function getCurrencySymbol(currency: CurrencyCode = 'USD'): string {
  const config = CURRENCY_CONFIGS[currency] || CURRENCY_CONFIGS.USD;
  return config.symbol;
}

/**
 * Parse a currency string and extract the amount
 * @param currencyString - The currency string to parse (e.g., "$1,234.56" or "₹1,234.56")
 * @returns The numeric amount
 */
export function parseCurrency(currencyString: string): number {
  // Remove currency symbols, commas, and spaces
  const cleanedString = currencyString.replace(/[$₹€£,\s]/g, '');
  return parseFloat(cleanedString) || 0;
}

/**
 * Format a large currency amount with abbreviations (K, M, B)
 * @param amount - The amount to format
 * @param currency - The currency code (default: USD)
 * @returns Abbreviated currency string
 */
export function formatCurrencyAbbreviated(
  amount: number,
  currency: CurrencyCode = 'USD'
): string {
  const config = CURRENCY_CONFIGS[currency] || CURRENCY_CONFIGS.USD;
  const symbol = config.symbol;

  if (amount >= 1_000_000_000) {
    return `${symbol}${(amount / 1_000_000_000).toFixed(1)}B`;
  } else if (amount >= 1_000_000) {
    return `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  } else if (amount >= 1_000) {
    return `${symbol}${(amount / 1_000).toFixed(1)}K`;
  } else {
    return `${symbol}${amount.toFixed(config.decimals)}`;
  }
}
